import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import type { RadarBubble } from '@/services/api/teacher'
import { teacherApi } from '@/services/api/teacher'

interface RequestSelectionDialogProps {
  isOpen: boolean
  onClose: () => void
  requestIds: string[]
  onSelect: (requestId: string, lessonRequestIds: string[]) => void
  bubble: RadarBubble | null
}

interface RequestDetails {
  id: string
  date: string | null
  subject_name: string | null
  klass_name: string | null
  room_name: string | null
  status: string
  total_lessons: number
  lessons_filled: number
  lessons_pending: number
  lessons_declined: number
  lesson_request_ids: string[]
}

export function RequestSelectionDialog({
  isOpen,
  onClose,
  requestIds,
  onSelect,
  bubble,
}: RequestSelectionDialogProps) {
  const [requests, setRequests] = useState<RequestDetails[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !requestIds.length) return

    setLoading(true)
    Promise.all(
      requestIds.map(id => teacherApi.getRequestDetails(id))
    )
      .then(setRequests)
      .catch(err => {
        console.error('Failed to load request details:', err)
        setRequests([])
      })
      .finally(() => setLoading(false))
  }, [isOpen, requestIds])

  if (!isOpen || !bubble) return null

  const statusLabels: Record<string, string> = {
    pending: 'Offen',
    accepted: 'Akzeptiert',
    declined: 'Abgelehnt',
    cancelled: 'Storniert',
    booked: 'Gebucht',
    partially_filled: 'Teilweise',
  }

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-green-100 text-green-800',
    declined: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
    booked: 'bg-blue-100 text-blue-800',
    partially_filled: 'bg-orange-100 text-orange-800',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-800">Anfrage auswählen</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {requestIds.length} Anfragen an diesem Tag
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition"
            aria-label="Schließen"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              Keine Anfragen gefunden
            </div>
          ) : (
            <div className="space-y-2">
              {requests.map(request => (
                <button
                  key={request.id}
                  onClick={() => onSelect(request.id, request.lesson_request_ids || [])}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm text-gray-800">
                      {request.date
                        ? new Date(request.date).toLocaleDateString('de-CH', {
                            weekday: 'short',
                            day: '2-digit',
                            month: '2-digit',
                          })
                        : '—'}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        statusColors[request.status] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {statusLabels[request.status] || request.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span>{request.subject_name || '—'}</span>
                    {request.klass_name && <span>| {request.klass_name}</span>}
                    {request.room_name && <span>| {request.room_name}</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                    <span>{request.total_lessons} Lektionen</span>
                    <span className="text-green-600">
                      {request.lessons_filled} akzeptiert
                    </span>
                    {request.lessons_pending > 0 && (
                      <span className="text-yellow-600">
                        {request.lessons_pending} offen
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  )
}
